<template>
  <div class="container-fluid">
    <div class="row justify-content-center">
      <div class="col-12 col-lg-10 col-xl-8">
        <div v-if="loading" class="text-center py-5">
          <div class="spinner-border text-primary" role="status">
            <span class="visually-hidden">Loading...</span>
          </div>
          <p class="mt-3 text-muted">Loading your application status...</p>
        </div>

        <div v-else-if="error" class="text-center py-5">
          <div class="alert alert-danger">
            <i class="fa fa-exclamation-triangle me-2"></i>
            {{ error }}
          </div>
        </div>

        <div v-else>
          <!-- Header -->
          <div class="card mb-4 shadow-sm">
            <div class="card-body text-center py-4">
              <h3 class="mb-1">Application Status</h3>
              <p class="text-muted mb-1">{{ statusData.driverName }}</p>
              <p class="text-muted small mb-0">{{ statusData.email }}</p>
              <p class="text-muted small mb-0" v-if="statusData.submittedDate">
                Submitted: {{ formatDate(statusData.submittedDate) }}
              </p>
            </div>
          </div>

          <!-- Overall Status Banner -->
          <div class="alert d-flex align-items-start mb-4 shadow-sm" :class="overallStatusAlertClass" role="alert">
            <i :class="overallStatusIcon" class="me-3 mt-1" style="font-size:1.6rem;flex-shrink:0;"></i>
            <div>
              <h5 class="mb-1">{{ overallStatusHeading }}</h5>
              <p class="mb-0">{{ overallStatusMessage }}</p>
              <template v-if="overallStatus === 'REJECTED'">
                <hr class="my-2">
                <div v-if="statusData.licenseRejectionMessage" class="mb-1">
                  <strong>License:</strong> {{ statusData.licenseRejectionMessage }}
                </div>
                <div v-if="statusData.insuranceRejectionMessage" class="mb-1">
                  <strong>Insurance:</strong> {{ statusData.insuranceRejectionMessage }}
                </div>
                <div v-if="statusData.vehiclePhotosRejectionMessage" class="mb-1">
                  <strong>Vehicle Photos:</strong> {{ statusData.vehiclePhotosRejectionMessage }}
                </div>
                <div v-if="statusData.backgroundCheckStatus === 'FAILED'" class="mt-1">
                  <strong>Background Check:</strong> Your background check did not pass. Please contact support for details.
                </div>
              </template>
            </div>
          </div>

          <!-- Progress Overview -->
          <div class="card mb-4 shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0"><i class="fa fa-tasks me-2"></i>Application Checklist</h5>
            </div>
            <div class="card-body p-0">
              <ul class="list-group list-group-flush">
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  <span><i class="fa fa-user me-2"></i>Personal Information</span>
                  <span class="badge rounded-pill"
                        :class="statusData.personalInfoComplete ? 'bg-success' : 'bg-warning'">
                                        {{ statusData.personalInfoComplete ? 'Complete' : 'Incomplete' }}
                                    </span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  <span><i class="fa fa-car me-2"></i>Vehicle Information</span>
                  <span class="badge rounded-pill"
                        :class="statusData.vehicleInfoComplete ? 'bg-success' : 'bg-warning'">
                                        {{ statusData.vehicleInfoComplete ? 'Complete' : 'Incomplete' }}
                                    </span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  <span><i class="fa fa-camera me-2"></i>Vehicle Photos</span>
                  <span class="badge rounded-pill"
                        :class="statusData.vehiclePhotosUploaded ? 'bg-success' : 'bg-warning'">
                                        {{ statusData.vehiclePhotosUploaded ? 'Uploaded' : 'Required' }}
                                    </span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  <span><i class="fa fa-id-badge me-2"></i>Profile Photo</span>
                  <span class="badge rounded-pill"
                        :class="statusData.profilePhotoUploaded ? 'bg-success' : 'bg-warning'">
                                        {{ statusData.profilePhotoUploaded ? 'Uploaded' : 'Required' }}
                                    </span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  <span><i class="fa fa-percent me-2"></i>GST/HST Number</span>
                  <span class="badge rounded-pill"
                        :class="statusData.taxNumberProvided ? 'bg-success' : 'bg-warning'">
                                        {{ statusData.taxNumberProvided ? 'Provided' : 'Required' }}
                                    </span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  <span><i class="fa fa-credit-card me-2"></i>Payment Setup</span>
                  <span class="badge rounded-pill" :class="statusData.stripeOnboarded ? 'bg-success' : 'bg-warning'">
                                        {{ statusData.stripeOnboarded ? 'Complete' : 'Required' }}
                                    </span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  <span><i class="fa fa-file me-2"></i>Documents Verification</span>
                  <span class="badge rounded-pill" :class="documentVerificationBadgeClass">
                                        {{ documentVerificationLabel }}
                                    </span>
                </li>
                <li class="list-group-item d-flex justify-content-between align-items-center">
                  <span><i class="fa fa-shield me-2"></i>Background Check</span>
                  <span class="badge rounded-pill" :class="backgroundCheckBadgeClass">
                                        {{ backgroundCheckLabel }}
                                    </span>
                </li>
              </ul>
            </div>
          </div>

          <!-- Vehicle Photos Upload Section -->
          <div class="card mb-4 shadow-sm" v-if="!statusData.vehiclePhotosUploaded">
            <div class="card-header bg-white">
              <h5 class="mb-0"><i class="fa fa-camera me-2"></i>Upload Vehicle Photos</h5>
            </div>
            <div class="card-body">
              <div class="alert alert-warning d-flex align-items-start gap-2 mb-3">
                <i class="fa fa-exclamation-triangle mt-1 flex-shrink-0"></i>
                <div>
                  <strong>Required for application approval.</strong> Our review team needs photos of your vehicle from all four angles before your application can be approved.
                </div>
              </div>
              <p class="text-muted mb-3">Upload exactly 4 photos — one from each angle: front, back, left side, and right side. Photos taken on your phone are resized automatically.</p>
              <div class="mb-3">
                <FilePickerControls input-id="vehicle-photos" accept="image/*" multiple
                                    :disabled="vehiclePhotosPreparing || vehiclePhotosFiles.length >= 4"
                                    camera-label="Take a photo"
                                    file-label="Choose photos"
                                    sole-label="Choose photos"
                                    button-class="btn btn-outline-primary"
                                    @picked="onVehiclePhotosSelected"/>
                <div class="form-text mt-1 d-flex align-items-center gap-2">
                  <span v-if="vehiclePhotosPreparing" class="text-muted">
                    <span class="spinner-border spinner-border-sm me-1"></span>Preparing photos...
                  </span>
                  <span v-else :class="vehiclePhotosFiles.length === 4 ? 'text-success fw-semibold' : 'text-muted'">
                    {{ vehiclePhotosFiles.length }} of 4 photos selected
                    <i v-if="vehiclePhotosFiles.length === 4" class="fa fa-check-circle ms-1"></i>
                  </span>
                  <span class="text-muted">&mdash; add them one at a time or all at once</span>
                </div>
              </div>
              <div v-if="vehiclePhotosPreviews.length > 0" class="row mb-3">
                <div v-for="(preview, index) in vehiclePhotosPreviews" :key="index" class="col-6 col-md-3 mb-2">
                  <div class="position-relative">
                    <img :src="preview" class="img-fluid rounded border" :alt="vehiclePhotoLabels[index] || ('Photo ' + (index + 1))"
                         style="height: 120px; width: 100%; object-fit: cover;">
                    <div class="position-absolute bottom-0 start-0 end-0 text-center"
                         style="background: rgba(0,0,0,0.55); border-radius: 0 0 4px 4px; padding: 3px 0;">
                      <small class="text-white" style="font-size: 0.7rem;">{{ vehiclePhotoLabels[index] || ('Photo ' + (index + 1)) }}</small>
                    </div>
                    <button type="button"
                            class="btn btn-sm btn-danger position-absolute top-0 end-0 m-1 p-0 lh-1"
                            style="width:22px;height:22px;"
                            @click="removeVehiclePhoto(index)"
                            title="Remove">
                      <i class="fa-solid fa-xmark" style="font-size:0.7rem;"></i>
                    </button>
                  </div>
                </div>
              </div>
              <div v-if="vehiclePhotosFiles.length > 0 && vehiclePhotosFiles.length !== 4" class="alert alert-warning mb-3">
                <i class="fa fa-exclamation-circle me-1"></i>
                All 4 photos are required. Please select one from each angle: front, back, left side, and right side.
              </div>
              <button class="btn btn-primary" @click="uploadVehiclePhotos"
                      :disabled="vehiclePhotosUploading || vehiclePhotosFiles.length !== 4">
                                <span v-if="vehiclePhotosUploading">
                                    <span class="spinner-border spinner-border-sm me-1"></span> Uploading...
                                </span>
                <span v-else><i class="fa fa-upload me-1"></i> Upload Photos</span>
              </button>
              <div v-if="vehiclePhotosError" class="alert alert-danger mt-2 mb-0">{{ vehiclePhotosError }}</div>
              <div v-if="vehiclePhotosSuccess" class="alert alert-success mt-2 mb-0">{{ vehiclePhotosSuccess }}</div>
            </div>
          </div>
          <div class="card mb-4 shadow-sm" v-else>
            <div class="card-header bg-white">
              <h5 class="mb-0"><i class="fa fa-camera me-2"></i>Vehicle Photos</h5>
            </div>
            <div class="card-body">
              <div class="alert alert-success mb-0"><i class="fa fa-check-circle me-2"></i>Vehicle photos have been
                uploaded successfully.
              </div>
            </div>
          </div>

          <!-- Profile Photo Upload Section -->
          <div class="card mb-4 shadow-sm" v-if="!statusData.profilePhotoUploaded">
            <div class="card-header bg-white">
              <h5 class="mb-0"><i class="fa fa-id-badge me-2"></i>Upload Profile Photo</h5>
            </div>
            <div class="card-body">
              <p class="text-muted mb-3">Please upload a clear photo of yourself. This will be shown to riders.</p>
              <div class="mb-3">
                <FilePickerControls input-id="profile-photo" accept="image/*"
                                    :disabled="profilePhotoPreparing"
                                    camera-label="Take a photo"
                                    file-label="Choose photo"
                                    sole-label="Choose photo"
                                    button-class="btn btn-outline-primary"
                                    @picked="onProfilePhotoSelected"/>
                <div v-if="profilePhotoPreparing" class="form-text mt-1 text-muted">
                  <span class="spinner-border spinner-border-sm me-1"></span>Preparing photo...
                </div>
              </div>
              <div v-if="profilePhotoPreview" class="mb-3 d-inline-flex flex-column align-items-center gap-2">
                <img :src="profilePhotoPreview" class="rounded-circle border" alt="Profile preview"
                     style="width: 120px; height: 120px; object-fit: cover;">
                <button type="button" class="btn btn-sm btn-outline-danger" @click="removeProfilePhoto">
                  <i class="fa-solid fa-xmark me-1"></i>Remove
                </button>
              </div>
              <button class="btn btn-primary" @click="uploadProfilePhoto"
                      :disabled="profilePhotoUploading || !profilePhotoFile">
                                <span v-if="profilePhotoUploading">
                                    <span class="spinner-border spinner-border-sm me-1"></span> Uploading...
                                </span>
                <span v-else><i class="fa fa-upload me-1"></i> Upload Photo</span>
              </button>
              <div v-if="profilePhotoError" class="alert alert-danger mt-2 mb-0">{{ profilePhotoError }}</div>
              <div v-if="profilePhotoSuccess" class="alert alert-success mt-2 mb-0">{{ profilePhotoSuccess }}</div>
            </div>
          </div>
          <div class="card mb-4 shadow-sm" v-else>
            <div class="card-header bg-white">
              <h5 class="mb-0"><i class="fa fa-id-badge me-2"></i>Profile Photo</h5>
            </div>
            <div class="card-body">
              <div class="alert alert-success mb-0"><i class="fa fa-check-circle me-2"></i>Profile photo has been
                uploaded successfully.
              </div>
            </div>
          </div>

          <!-- Tax Registration Section. Nothing renders once a number is on file: the checklist above
               already carries a "GST/HST Number — Provided" row, and a second card saying the same
               thing is duplication on the same screen. -->
          <DriverTaxRegistrationCard v-if="!statusData.taxNumberProvided" @saved="fetchStatus" />

          <!-- Stripe Onboarding Section -->
          <div class="card mb-4 shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0"><i class="fa fa-credit-card me-2"></i>Payment Setup (Stripe)</h5>
            </div>
            <div class="card-body">
              <div v-if="statusData.stripeOnboarded" class="alert alert-success mb-0">
                <i class="fa fa-check-circle me-2"></i>Your Stripe payment account has been set up successfully. You're
                ready to receive payments.
              </div>
              <div v-else>
                <p class="text-muted mb-3">Complete your Stripe payment setup to receive ride payments. Click the button
                  below to continue your Stripe onboarding.</p>
                <button class="btn btn-primary" @click="openStripeOnboarding" :disabled="stripeLoading">
                                    <span v-if="stripeLoading">
                                        <span class="spinner-border spinner-border-sm me-1"></span> Loading...
                                    </span>
                  <span v-else><i class="fa fa-external-link me-1"></i> Complete Stripe Setup</span>
                </button>
                <div v-if="stripeError" class="alert alert-danger mt-2 mb-0">{{ stripeError }}</div>
              </div>
            </div>
          </div>

          <!-- Documents Verification Section -->
          <div class="card mb-4 shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0"><i class="fa fa-file me-2"></i>Documents Verification</h5>
            </div>
            <div class="card-body">
              <div v-if="statusData.documentVerificationStatus === 'NOT_STARTED'" class="alert alert-info mb-0">
                <i class="fa fa-info-circle me-2"></i>Your document verification has not been initiated yet.
                This will be started once you upload your license, insurance, and vehicle photos.
              </div>
              <div v-else-if="statusData.documentVerificationStatus === 'PENDING_VERIFICATION'" class="alert alert-warning mb-0">
                <i class="fa fa-clock-o me-2"></i>Your documents are under review. This typically takes 2-3 business days.
              </div>
              <div v-else-if="statusData.documentVerificationStatus === 'COMPLETE'" class="alert alert-success mb-0">
                <i class="fa fa-check-circle me-2"></i>Your license, insurance, and vehicle photos have been verified.
              </div>
              <div v-else-if="statusData.documentVerificationStatus === 'REJECTED'">
                <div class="alert alert-danger mb-3">
                  <i class="fa fa-times-circle me-2"></i>One or more documents require changes. Please review the details below and resubmit.
                </div>
                <div v-if="statusData.licenseRejectionMessage" class="ms-2 mb-1">
                  <b>Driver's License:</b> {{statusData.licenseRejectionMessage}}
                </div>
                <div v-if="statusData.insuranceRejectionMessage" class="ms-2 mb-1">
                  <b>Insurance Document:</b> {{statusData.insuranceRejectionMessage}}
                </div>
                <div v-if="statusData.vehiclePhotosRejectionMessage" class="ms-2 mb-1">
                  <b>Vehicle Photos:</b> {{statusData.vehiclePhotosRejectionMessage}}
                </div>
                <div class="alert alert-info mt-3 mb-0">
                  <i class="fa fa-info-circle me-2"></i>
                  <strong>Need help?</strong> If you have questions about the rejection or believe it was made in error,
                  please email <a href="mailto:support@asher-rides.com" class="alert-link">support@asher-rides.com</a>
                  with your <strong>full name</strong>, <strong>email address</strong>, and any
                  <strong>supporting documents</strong> for our team to review your enquiry.
                </div>
              </div>
            </div>
          </div>

          <!-- Background Check Section -->
          <div class="card mb-4 shadow-sm">
            <div class="card-header bg-white">
              <h5 class="mb-0"><i class="fa fa-shield me-2"></i>Background Check</h5>
            </div>
            <div class="card-body">
              <div v-if="statusData.backgroundCheckStatus === 'NOT_STARTED'" class="alert alert-info mb-0">
                <i class="fa fa-info-circle me-2"></i>Your background check has not been initiated yet. This will be
                started once your application is reviewed by our team.
              </div>
              <div v-else-if="statusData.backgroundCheckStatus === 'IN_PROGRESS'" class="mb-0">
                <div v-if="backgroundCheckLink" class="alert alert-info mb-0">
                  <i class="fa fa-envelope me-2"></i>
                  We've emailed you a link to start your background check with Certn, our background
                  check provider. You can also start it right here &mdash; it only takes a few minutes,
                  and your application can't move forward until it's done. Once you've completed it,
                  results typically take 3-5 business days.
                  <div class="mt-3">
                    <a :href="backgroundCheckLink" target="_blank" rel="noopener noreferrer"
                       class="btn btn-primary">
                      <i class="fa fa-shield me-1"></i>Start Your Background Check
                    </a>
                  </div>
                </div>
                <div v-else class="alert alert-warning mb-0">
                  <i class="fa fa-clock-o me-2"></i>Your background check is in progress. Check your email
                  for the link from our team to complete it. Results typically take 3-5 business days.
                </div>
              </div>
              <div v-else-if="statusData.backgroundCheckStatus === 'PASSED'" class="alert alert-success mb-0">
                <i class="fa fa-check-circle me-2"></i>Your background check has been completed and passed.
              </div>
              <div v-else-if="statusData.backgroundCheckStatus === 'ACTION_REQUIRED'" class="alert alert-warning mb-0">
                <i class="fa fa-exclamation-triangle me-2"></i>Your background check requires additional action.
                Please check your email for instructions from our background check provider, or contact
                <a href="mailto:support@asher-rides.com">support@asher-rides.com</a> if you need help.
              </div>
              <div v-else-if="statusData.backgroundCheckStatus === 'FAILED'" class="alert alert-danger mb-0">
                <i class="fa fa-times-circle me-2"></i>Your background check did not pass. Please contact support for
                more information.
              </div>
            </div>
          </div>

          <!-- Referral Dashboard Section -->
          <DriverReferralDashboard />

          <!-- Support Section -->
          <div class="card mb-4 shadow-sm">
            <div class="card-body text-center">
              <p class="text-muted mb-1">Need help? Contact us at</p>
              <a href="mailto:support@asher-rides.com" class="text-primary">support@asher-rides.com</a>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import moment from "moment/moment";
import DriverReferralDashboard from './DriverReferralDashboard.vue';
import DriverTaxRegistrationCard from './DriverTaxRegistrationCard.vue';
import FilePickerControls from './FilePickerControls.vue';
import {preparePickedFile, IMAGE_TYPES} from '../fileUploadValidation';

const loading = ref(true);
const error = ref(null);
const statusData = ref({});

const vehiclePhotosFiles = ref([]);
const vehiclePhotosPreviews = ref([]);
const vehiclePhotosUploading = ref(false);
const vehiclePhotosError = ref(null);
const vehiclePhotosSuccess = ref(null);
const vehiclePhotoLabels = ['Front', 'Back', 'Left Side', 'Right Side'];
const vehiclePhotosPreparing = ref(false);
const REQUIRED_VEHICLE_PHOTOS = 4;

const profilePhotoFile = ref(null);
const profilePhotoPreview = ref(null);
const profilePhotoUploading = ref(false);
const profilePhotoError = ref(null);
const profilePhotoSuccess = ref(null);
const profilePhotoPreparing = ref(false);

const stripeLoading = ref(false);
const stripeError = ref(null);

const checklistComplete = computed(() => {
  const s = statusData.value;
  return s.personalInfoComplete &&
      s.vehicleInfoComplete &&
      s.vehiclePhotosUploaded &&
      s.profilePhotoUploaded &&
      s.stripeOnboarded &&
      s.taxNumberProvided &&
      s.documentVerificationStatus === 'COMPLETE';
});

const overallStatus = computed(() => {
  const s = statusData.value.overallStatus;
  if (s === 'COMPLETE') return 'APPROVED';
  if (s === 'REJECTED') return 'REJECTED';
  return checklistComplete.value ? 'PENDING' : 'INCOMPLETE';
});

const overallStatusAlertClass = computed(() => ({
  'APPROVED':   'alert-success',
  'REJECTED':   'alert-danger',
  'PENDING':    'alert-warning',
  'INCOMPLETE': 'alert-info',
}[overallStatus.value] || 'alert-info'));

const overallStatusIcon = computed(() => ({
  'APPROVED':   'fa fa-check-circle text-success',
  'REJECTED':   'fa fa-times-circle text-danger',
  'PENDING':    'fa fa-hourglass-half text-warning',
  'INCOMPLETE': 'fa fa-clipboard-list text-info',
}[overallStatus.value] || 'fa fa-clipboard-list text-info'));

const overallStatusHeading = computed(() => ({
  'APPROVED':   'Application Approved',
  'REJECTED':   'Application Not Approved',
  'PENDING':    'Application Under Review',
  'INCOMPLETE': 'Application Incomplete',
}[overallStatus.value] || 'Application Incomplete'));

const overallStatusMessage = computed(() => ({
  'APPROVED':   'Congratulations! Your application has been approved. You can now log in and start accepting rides.',
  'REJECTED':   'Unfortunately your application was not approved. See the reason(s) below and contact support if you have questions.',
  'PENDING':    'Your application is being reviewed by our team. We\'ll send you an email once a decision has been made.',
  'INCOMPLETE': 'Please complete all the required steps below before your application can be submitted for review.',
}[overallStatus.value] || ''));

// Rendered as an href, and the value is typed by an admin. Anything but https -- a javascript: URL
// above all -- must never reach the DOM. The server validates on the way in; this is the second gate.
const backgroundCheckLink = computed(() => {
  const url = statusData.value.backgroundCheckUrl;
  return typeof url === 'string' && url.startsWith('https://') ? url : null;
});

const backgroundCheckLabel = computed(() => ({
  'NOT_STARTED': 'Not Started',
  'IN_PROGRESS': 'In Progress',
  'PASSED': 'Passed',
  'FAILED': 'Failed',
  'ACTION_REQUIRED': 'Action Required'
}[statusData.value.backgroundCheckStatus] || 'Not Started'));

const backgroundCheckBadgeClass = computed(() => ({
  'NOT_STARTED': 'bg-secondary',
  'IN_PROGRESS': 'bg-warning',
  'PASSED': 'bg-success',
  'FAILED': 'bg-danger',
  'ACTION_REQUIRED': 'bg-warning text-dark'
}[statusData.value.backgroundCheckStatus] || 'bg-secondary'));

const documentVerificationLabel = computed(() => ({
  'NOT_STARTED': 'Not Started',
  'PENDING_VERIFICATION': 'Pending Verification',
  'COMPLETE': 'Complete',
  'REJECTED': 'Rejected'
}[statusData.value.documentVerificationStatus] || 'Not Started'));

const documentVerificationBadgeClass = computed(() => ({
  'NOT_STARTED': 'bg-secondary',
  'PENDING_VERIFICATION': 'bg-warning',
  'COMPLETE': 'bg-success',
  'REJECTED': 'bg-danger'
}[statusData.value.documentVerificationStatus] || 'bg-secondary'));

const fetchStatus = async () => {
  try {
    loading.value = true;
    const response = await fetch('/api/driver/application-status');
    if (!response.ok) {
      throw new Error('Unable to load application status. Please sign in again.');
    }
    statusData.value = await response.json();
  } catch (e) {
    error.value = e.message || 'Failed to load application status.';
  } finally {
    loading.value = false;
  }
};

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    return moment(dateStr).format('MMMM Do, YYYY [at] h:mm a');
  } catch {
    return dateStr;
  }
};

/**
 * Adds picked vehicle photos to the pending set, downscaling each before it is held for upload.
 *
 * Photos are appended rather than replacing the set, so the camera — which returns one frame at a
 * time — reaches four the same way the gallery does in a single multi-select.
 *
 * @param {Event} event the change event from the camera or file input
 */
const onVehiclePhotosSelected = async (event) => {
  const input = event.target;
  const picked = Array.from(input.files || []);
  if (picked.length === 0) return;

  vehiclePhotosError.value = null;
  vehiclePhotosPreparing.value = true;
  try {
    if (vehiclePhotosFiles.value.length >= REQUIRED_VEHICLE_PHOTOS) {
      vehiclePhotosError.value =
          `You already have ${REQUIRED_VEHICLE_PHOTOS} photos. Remove one before adding another.`;
      return;
    }

    // Reasons are collected rather than shown per file: SweetAlert replaces whatever modal is
    // open, so firing one per rejected photo leaves the driver seeing only the last of them.
    const rejected = [];
    let skippedForSpace = 0;

    for (const candidate of picked) {
      // Recounted each time round rather than snapshotted: a photo can be removed while this loop
      // is still awaiting, and the count is what the Upload button is gated on.
      if (vehiclePhotosFiles.value.length >= REQUIRED_VEHICLE_PHOTOS) {
        skippedForSpace += 1;
        continue;
      }
      const file = await preparePickedFile(candidate, null, IMAGE_TYPES,
          {subject: 'vehicle photo', silent: rejected});
      if (!file) continue;
      vehiclePhotosFiles.value.push(file);
      vehiclePhotosPreviews.value.push(previewUrlFor(file));
    }

    const problems = [...rejected];
    if (skippedForSpace > 0) {
      problems.push(`${skippedForSpace} photo(s) were not added — only ${REQUIRED_VEHICLE_PHOTOS} are needed in total.`);
    }
    if (problems.length > 0) vehiclePhotosError.value = problems.join(' ');
  } finally {
    vehiclePhotosPreparing.value = false;
    // Cleared unconditionally so re-picking the same photo still raises a change event.
    input.value = '';
  }
};

/**
 * Creates an inline preview URL for a picked photo.
 *
 * An object URL rather than a base64 data URL: base64 is about a third larger than the file and is
 * held as a live string for as long as the preview is on screen, so four phone photos would retain
 * several megabytes of strings on the same page that just encoded them — which on iOS is the side
 * of the memory budget that actually matters. Every URL made here is revoked in {@link revokePreview}.
 *
 * @param {File} file the file to preview
 * @returns {string} an object URL for the file
 */
const previewUrlFor = (file) => URL.createObjectURL(file);

/**
 * Releases a preview URL created by {@link previewUrlFor}.
 *
 * @param {string} url the object URL to release
 */
const revokePreview = (url) => {
  if (url) URL.revokeObjectURL(url);
};

const removeVehiclePhoto = (index) => {
  revokePreview(vehiclePhotosPreviews.value[index]);
  vehiclePhotosFiles.value = vehiclePhotosFiles.value.filter((_, i) => i !== index);
  vehiclePhotosPreviews.value = vehiclePhotosPreviews.value.filter((_, i) => i !== index);
};

const removeProfilePhoto = () => {
  revokePreview(profilePhotoPreview.value);
  profilePhotoFile.value = null;
  profilePhotoPreview.value = null;
};

const uploadVehiclePhotos = async () => {
  if (vehiclePhotosFiles.value.length === 0) return;
  vehiclePhotosUploading.value = true;
  vehiclePhotosError.value = null;
  vehiclePhotosSuccess.value = null;
  try {
    const formData = new FormData();
    vehiclePhotosFiles.value.forEach(file => {
      formData.append('vehiclePhotos', file);
    });
    const response = await fetch('/api/driver/application-status/upload-vehicle-photos', {
      method: 'POST',
      body: formData
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to upload vehicle photos.');
    }
    vehiclePhotosSuccess.value = 'Vehicle photos uploaded successfully!';
    statusData.value.vehiclePhotosUploaded = true;
  } catch (e) {
    vehiclePhotosError.value = e.message || 'Failed to upload vehicle photos.';
  } finally {
    vehiclePhotosUploading.value = false;
  }
};

/**
 * Accepts a picked profile photo, downscaling it before it is held for upload.
 *
 * @param {Event} event the change event from the camera or file input
 */
const onProfilePhotoSelected = async (event) => {
  const input = event.target;
  const picked = input.files?.[0];
  if (!picked) return;

  profilePhotoError.value = null;
  profilePhotoPreparing.value = true;
  try {
    const file = await preparePickedFile(picked, null, IMAGE_TYPES, {subject: 'profile photo'});
    if (!file) return;
    profilePhotoFile.value = file;
    revokePreview(profilePhotoPreview.value);
    profilePhotoPreview.value = previewUrlFor(file);
  } finally {
    profilePhotoPreparing.value = false;
    // Cleared unconditionally so re-picking the same photo still raises a change event.
    input.value = '';
  }
};

const uploadProfilePhoto = async () => {
  if (!profilePhotoFile.value) return;
  profilePhotoUploading.value = true;
  profilePhotoError.value = null;
  profilePhotoSuccess.value = null;
  try {
    const formData = new FormData();
    formData.append('profilePhoto', profilePhotoFile.value);
    const response = await fetch('/api/driver/application-status/upload-profile-photo', {
      method: 'POST',
      body: formData
    });
    if (!response.ok) {
      const text = await response.text();
      throw new Error(text || 'Failed to upload profile photo.');
    }
    profilePhotoSuccess.value = 'Profile photo uploaded successfully!';
    statusData.value.profilePhotoUploaded = true;
  } catch (e) {
    profilePhotoError.value = e.message || 'Failed to upload profile photo.';
  } finally {
    profilePhotoUploading.value = false;
  }
};

const openStripeOnboarding = async () => {
  stripeLoading.value = true;
  stripeError.value = null;
  const popup = window.open('', '_blank');

  try {
    const response = await fetch('/api/driver/application-status/stripe-link');
    if (!response.ok) {
      throw new Error('Failed to get Stripe onboarding link.');
    }
    const data = await response.json();
    if (data.url) {
      popup.location.href = data.url;
    } else {
      popup.close();
      throw new Error('No Stripe onboarding URL received.');
    }
  } catch (e) {
    if (popup) popup.close();
    stripeError.value = e.message || 'Failed to open Stripe onboarding.';
  } finally {
    stripeLoading.value = false;
  }
};

onMounted(fetchStatus);
</script>